import type { ReactNode } from 'react';
import { fmtMoney } from '../../lib/nta';
import { chunkSites, noRecSummary, NTA_NO_REC_WORDING as W, type NtaReportSite } from '../../lib/ntaClientReport';

// Shield NTA client report — the two-page Dy-Mark layout (spec 8.1, C-02).
// C-01: the props are the ONLY data this component receives, and none of them
// is a tariff, tariff code, rate or component. Keep it that way. NMIs and site
// addresses appear only in the no-recommendation site summary and the mixed
// note (client request #4), and only when the page passes them.
// Variants: 'recommendation' (Dy-Mark, plus the optional mixed note) and
// 'no_recommendation' (D-06 draft: same branding, a site summary page between
// the front and back pages). All no-recommendation copy is in lib/ntaClientReport.
// C-03: the National Customer Code logo is removed; the logo row re-spaces.
// ponytail: Plus Jakarta Sans stands in for the PDF's geometric face (already
// loaded app-wide); swap in Choice Energy's brand font file when supplied.

export type NtaEnergyManager = {
  name: string;
  title: string;
  phone: string;
  mobile: string;
  email: string;
};

export type NtaReportProps = {
  customerName: string;
  savings: number;
  energyManager: NtaEnergyManager;
  variant?: 'recommendation' | 'no_recommendation';
  /** no_recommendation: the customer's sites with customer-facing outcome wording. */
  sites?: NtaReportSite[];
  /** no_recommendation: false drops the NMI column from the site summary (C-01). */
  showNmis?: boolean;
  /** recommendation: the mixed-client note (NMIs with no change recommended). */
  note?: string | null;
};

const TEAL = '#2B6580';
const YELLOW = '#F1D13D';
const FONT = '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, sans-serif';

const AWARDS: Array<[string, string, string]> = [
  ['Financial Review', 'Fast 100', '2020'],
  ['Smart Company', 'Smart 50 List', '2018'],
  ['Financial Times', 'High-Growth Companies', '2020'],
  ['ABA10', 'Service Excellence Winner', '2018'],
];

const pageStyle: React.CSSProperties = {
  width: '210mm',
  height: '297mm',
  overflow: 'hidden',
  position: 'relative',
  fontFamily: FONT,
  color: '#1f2937',
  background: '#fff',
  breakAfter: 'page',
  boxSizing: 'border-box',
};

const body: React.CSSProperties = { fontSize: 13.5, marginTop: 12, lineHeight: 1.5 };

type BandProps = { customerName: string; main: string; sub: string; testId: string };

/** Yellow band with the circle overlapping it — geometry measured off the Dy-Mark PDF render. */
const Band = ({ customerName, main, sub, testId }: BandProps) => (
  <div style={{ position: 'relative', marginTop: '16mm', height: '31mm', background: YELLOW }}>
    <div style={{ position: 'absolute', left: '24mm', right: '95mm', top: 0, bottom: 0, display: 'flex', alignItems: 'center',
      color: TEAL, fontWeight: 700, fontSize: 17 }}>
      {customerName}
    </div>
    <div style={{ position: 'absolute', right: '28mm', top: '-12mm', width: '58mm', height: '58mm', borderRadius: '50%',
      background: TEAL, color: YELLOW, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', textAlign: 'center' }}>
      <div data-testid={testId} style={{ fontSize: 28 }}>{main}</div>
      <div style={{ fontSize: 21, fontStyle: 'italic', color: '#fff' }}>{sub}</div>
    </div>
  </div>
);

/** Fixed lower half of page 1: Network Tariff Analysis copy, logo row, disclaimer. */
const AnalysisSection = () => (
  <div style={{ padding: '14mm 16mm 0' }}>
    <h2 style={{ color: TEAL, fontSize: 29, margin: 0, fontWeight: 700 }}>Network Tariff Analysis</h2>
    <p style={{ color: TEAL, fontWeight: 700, fontSize: 13.5, marginTop: 14, lineHeight: 1.5 }}>
      Charges outside your retail contract, such as demand and network charges, can account for up to 60% of your
      total bill cost. Without independent metering, there is no way to maximise savings on these components of
      the bill.
    </p>
    <p style={body}>
      As part of the Shield Metering and Monitoring service we provide you, Choice Energy undertakes six-monthly
      assessments of your network charges to ensure the costs imposed by your electricity distributor best reflects
      your usage profile. We do this every six months because your business energy may have changed since it was
      last set - for example you may have taken over a site from a larger energy user or you’ve implemented a new
      piece of equipment that has reduced your demand which might mean you’re eligible for a more optimal tariff,
      saving you more.
    </p>

    {/* Logo row — National Customer Code removed (C-03) */}
    <div data-testid="nta-report-logos" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginTop: 20, gap: 12 }}>
      <img src="/nta/approved-seller.png" alt="New Energy Tech Approved Seller" style={{ width: '24mm', height: '24mm' }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3mm 6mm', fontSize: 8.5, lineHeight: 1.25 }}>
        {AWARDS.map(([a, b, year]) => (
          <div key={a} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <img src="/nta/trophy.png" alt="" style={{ width: '7mm' }} />
            <span>{a}<br />{b} <em>{year}</em></span>
          </div>
        ))}
      </div>
      <img src="/nta/choice-energy.png" alt="Choice Energy" style={{ width: '38mm' }} />
    </div>

    <p style={{ fontSize: 7.5, marginTop: 18, lineHeight: 1.45, textAlign: 'justify' }}>
      Disclaimer: *Choice Energy (“CE”) utilises complex systems to analyse your meter data and calculate annual
      network charges based on your electricity usage. *We use available contract data, market data and meter data
      to verify tariff readings. The data is provided by third parties and Choice Energy will not take
      responsibility for incorrect results due to the information provided.* *CE updates its Network Tariff Review
      system from time to time, which may alter or enhance the functionality and form.* *While Choice Energy has
      made an assessment based on the recent published tariffs, I understand there are no guarantees as to what the
      future level of savings may be because the tariff settings are set by the network providers.*
    </p>
  </div>
);

type FrontPageProps = { title: string; customerName: string; band: ReactNode; children: ReactNode };

const FrontPage = ({ title, customerName, band, children }: FrontPageProps) => (
  <section style={pageStyle} data-testid="nta-report-page-1">
    <div style={{ padding: '18mm 16mm 0' }}>
      <h1 style={{ color: TEAL, fontSize: 30, lineHeight: 1.1, margin: 0, fontWeight: 700 }}>
        {title}
        <br />
        <span style={{ fontWeight: 400 }}>{customerName}</span>
      </h1>
      {children}
    </div>
    {band}
    <AnalysisSection />
  </section>
);

type SitePageProps = { rows: NtaReportSite[]; first: boolean; showNmis: boolean };

/** D-06 draft: site summary page(s) of the no-recommendation report. NMI, address and outcome only (C-01). */
const SitePage = ({ rows, first, showNmis }: SitePageProps) => (
  <section style={pageStyle} data-testid="nta-report-sites">
    <div style={{ padding: '18mm 16mm 0' }}>
      <h2 style={{ color: TEAL, fontSize: 29, margin: 0, fontWeight: 700 }}>
        {W.siteTableHeading}{first ? '' : ' (continued)'}
      </h2>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '8mm', fontSize: 11 }}>
        <thead>
          <tr style={{ background: TEAL, color: '#fff', textAlign: 'left' }}>
            {W.siteTableColumns.filter((c) => showNmis || c !== 'NMI').map((c) => <th key={c} style={{ padding: '2.5mm 3mm', fontWeight: 700 }}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.nmi} style={{ borderBottom: '0.3mm solid #e5e7eb', verticalAlign: 'top' }}>
              {showNmis && <td style={{ padding: '2.5mm 3mm', whiteSpace: 'nowrap', fontWeight: 700, color: TEAL }}>{s.nmi}</td>}
              <td style={{ padding: '2.5mm 3mm' }}>{s.address || '—'}</td>
              <td style={{ padding: '2.5mm 3mm' }}>{s.detail.map((d) => <div key={d}>{d}</div>)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const BackPage = ({ em }: { em: NtaEnergyManager }) => (
  <section style={{ ...pageStyle, background: TEAL, color: '#fff' }} data-testid="nta-report-page-2">
    <div style={{ position: 'absolute', left: '50%', top: '64mm', width: '4mm', height: '61mm', marginLeft: '-2mm',
      borderRadius: '50%', background: '#0F4C81' }} />
    <div style={{ position: 'absolute', top: '128mm', left: 0, right: 0, textAlign: 'center' }}>
      <p style={{ fontStyle: 'italic', fontSize: 16 }}>For queries, speak with your dedicated energy manager:</p>
      <p style={{ fontSize: 26, fontWeight: 700, marginTop: 18 }}>{em.name || '—'}</p>
      <div style={{ fontSize: 14, lineHeight: 2.1, marginTop: 10 }}>
        <div>{em.title || '—'},</div>
        <div>Choice Energy</div>
        <div>Phone: {em.phone || '—'}</div>
        <div>Mobile: {em.mobile || '—'}</div>
        <div>Email address: {em.email || '—'}</div>
      </div>
    </div>
    <p style={{ position: 'absolute', left: '16mm', right: '16mm', bottom: '36mm', fontSize: 7.5, fontStyle: 'italic', lineHeight: 1.45 }}>
      <strong>Confidentiality:</strong> This report and any attachments may contain confidential, proprietary or
      legally privileged information. If you are not the intended recipient, you must not keep, forward, disclose,
      adapt or copy the material and any such action is unorthorised and prohibited. If you have received this report
      in error, please immediately advise the sender by return email and delete any attachments from our system.
    </p>
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '28mm', background: '#fff',
      borderTop: `2mm solid ${YELLOW}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 16mm', color: TEAL }}>
      <div style={{ fontSize: 11 }}>
        <strong>Choice Energy Pty Ltd, Level 6/580 St Kilda Rd Melbourne VIC 3004</strong>
        <div style={{ fontStyle: 'italic', fontSize: 10 }}>ABN: 64 935 373830 ACN No: 166 176 325</div>
      </div>
      <img src="/nta/choice-energy.png" alt="Choice Energy" style={{ width: '28mm' }} />
    </div>
  </section>
);

export const NtaReport = ({
  customerName, savings, energyManager: em, variant = 'recommendation', sites = [], showNmis = false, note = null,
}: NtaReportProps) => {
  if (variant === 'no_recommendation') {
    return (
      <div className="nta-report" data-testid="nta-report" data-variant="no_recommendation">
        {/* D-06 draft wording — every sentence below comes from NTA_NO_REC_WORDING */}
        <FrontPage title={W.title} customerName={customerName}
          band={<Band customerName={customerName} main={W.circleMain} sub={W.circleSub} testId="nta-report-headline" />}>
          <p style={{ ...body, marginTop: 18 }}>
            <strong>{W.intro(customerName)}</strong> {W.introRest}
          </p>
          <p style={body}>{noRecSummary(sites)}</p>
          <p style={body}>{W.nextReview}</p>
        </FrontPage>
        {chunkSites(sites).map((rows, i) => <SitePage key={rows[0].nmi} rows={rows} first={i === 0} showNmis={showNmis} />)}
        <BackPage em={em} />
      </div>
    );
  }

  const amount = fmtMoney(savings);
  return (
    <div className="nta-report" data-testid="nta-report" data-variant="recommendation">
      <FrontPage title="Business Network Savings Identified for" customerName={customerName}
        band={<Band customerName={customerName} main={amount} sub="1 Year Savings" testId="nta-report-savings" />}>
        <p style={{ ...body, marginTop: 18 }}>
          <strong>Choice Energy has conducted a Network Tariff and Demand Analysis review for {customerName}</strong> to
          ensure the costs imposed by your electricity distributor(s) best reflects your usage profile.
        </p>
        <p style={body}>
          The projected savings are <strong>{amount}</strong> for the first year.
        </p>
        <p style={body}>
          Upon your approval Choice Energy will work to implement this more optimal tariff on your behalf.
        </p>
        {/* Mixed client — D-06 draft wording: NMIs with no change recommended */}
        {note && (
          <p data-testid="nta-report-note" style={{ fontSize: 10.5, marginTop: 10, lineHeight: 1.4, color: '#4b5563' }}>
            {note}
          </p>
        )}
      </FrontPage>
      <BackPage em={em} />
    </div>
  );
};
